import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiConstants } from '../../shared/constants/api-constants';
import { ISkillAuthorizationResponse } from '../models/skill-authorization-response';
import { ISkillSaveRequest } from '../models/skill-save-request';
import { SkillDetail } from '../models/skill-detail.model';
import { Skill } from '../models/skill';
import { UpdateTrainingItemsStatusDto } from '../models/update-training-items-status.dto';
import { UnassignTrainingItemGroupRequest } from '../models/unassign-training-item-group.dto';
import { AuditTrialDetails } from '../../shared/models/audits/audit-trial-details';

@Injectable({
  providedIn: 'root'
})
export class SkillService {

  constructor(private httpClient: HttpClient) { }

  getSkillAuthorization(): Observable<ISkillAuthorizationResponse> {
    return this.httpClient.get<ISkillAuthorizationResponse>(ApiConstants.Skill_Authorization_Api);
  }

  saveSkill(payload: ISkillSaveRequest): Observable<any> {
    return this.httpClient.post(ApiConstants.Skill_Save_Api, payload);
    }

  updateSkill(payload: ISkillSaveRequest): Observable<any> {
    return this.httpClient.put(ApiConstants.Skill_Update_Api, payload);
  }

  /**
   * Fetch skill details by skill code
   * @param skillCode - The unique identifier for the skill
   * @returns Observable of skill details
   */
  getSkillByCode(skillCode: string): Observable<SkillDetail> {
    const params = new HttpParams().set('skillCode', skillCode);
    return this.httpClient.get<SkillDetail>(ApiConstants.Skill_Api, { params });
  }

  getScopes(): Observable<any> {
    return this.httpClient.get(ApiConstants.Skill_Scopes_Api);
  }

    getSkillByID(skillId: string): Observable<SkillDetail> {
    return this.httpClient.get<SkillDetail>(ApiConstants.Skill_Api+'/'+ skillId);
  }

  getLatestDocumentDetail(documentNumberList: string[]): Observable<any> {
      return this.httpClient.post(ApiConstants.Get_List_Of_DocumentLatestVersion_Api,documentNumberList);
  }

  updateLinkedTrainingItems(skill:Skill): Observable<any> {
    return this.httpClient.post(ApiConstants.Skill_UpdateLinkedTrainingItems_Api, skill);
  }

  /**
   * Fetch linked training items for a skill by skill code
   * @param skillCode - The unique identifier for the skill
   * @returns Observable of array of linked training items
   */
  getLinkedTrainingItems(skillCode: string): Observable<any[]> {
    const url = ApiConstants.Skill_LinkedTrainingItems_Api.replace('{skillCode}', skillCode);
    return this.httpClient.get<any[]>(url);
  }

  /**
   * Batch-update the status of one or more linked training items on a skill.
   * @param skillCode - The skill code
   * @param payload - Training item ids and the new status to apply
   * @returns Observable of the update response
   */
  updateTrainingItemsStatus(skillCode: string, payload: UpdateTrainingItemsStatusDto): Observable<any> {
    const url = ApiConstants.Skill_UpdateTrainingItemsStatus_Api.replace('{skillCode}', skillCode);
    return this.httpClient.put(url, payload);
  }

  unassignTrainingItemGroups(skillCode: string, payload: UnassignTrainingItemGroupRequest): Observable<any> {
    const url = ApiConstants.Skill_UnassignTrainingItemGroups_Api.replace('{skillCode}', skillCode);
    return this.httpClient.put(url, payload);
  }


  /**
   * Fetch the audit trail rows for a single skill, used by the Audit tab on
   * the Skill Details page.
   * @param skillId - The skill identifier (Guid)
   */
  /**
   * Fetch the audit trail for a skill (used by the Audit tab).
   * @param skillCode - Unique, human-readable skill code; doubles as the audit file identifier.
   */
  getSkillAudit(skillCode: string): Observable<AuditTrialDetails[]> {
    const url = `${ApiConstants.Skill_Audit_Trail_Api}?skillCode=${encodeURIComponent(skillCode)}`;
    return this.httpClient.get<AuditTrialDetails[]>(url);
  }
}
